
import { ApiClient } from './types';
import { Trek, Company, TrekPackage } from '@/lib/types/trek-types';
import { ContactFormData, ContactInquiry, InquiryStatus } from '@/lib/types/contact-types';
import { getImageUrl, getCommonSearchTerms, getSearchSuggestions, recordSuggestionClick } from './supabase/utils';
import { getTrekById, getAllTreks, getSimilarTreks, searchTreks } from './supabase/trek-api';
import { getCompanyById, getAllCompanies, getFeaturedCompanies } from './supabase/company-api';
import { getPackagesByTrekId, getPackagesByCompanyId, getPackageById } from './supabase/package-api';
import { submitContactInquiry, getContactInquiries, updateInquiryStatus } from './supabase/contact-api';
import { supabase } from '@/integrations/supabase/client';

class SupabaseApiClient implements ApiClient {
  async getImageUrl(path: string, fallbackUrl?: string): Promise<string> {
    return getImageUrl(path, fallbackUrl);
  }

  async getTrekById(id: string): Promise<Trek | null> {
    return getTrekById(id);
  }

  async getAllTreks(): Promise<Trek[]> {
    return getAllTreks();
  }

  async getSimilarTreks(trekId: string, limit = 3): Promise<Trek[]> {
    return getSimilarTreks(trekId, limit);
  }

  async getCompanyById(id: string): Promise<Company | null> {
    return getCompanyById(id);
  }

  async getAllCompanies(): Promise<Company[]> {
    return getAllCompanies();
  }
  
  async getFeaturedCompanies(limit = 4): Promise<Company[]> {
    return getFeaturedCompanies(limit);
  }

  async getPackagesByTrekId(trekId: string): Promise<TrekPackage[]> {
    return getPackagesByTrekId(trekId);
  }

  async getPackagesByCompanyId(companyId: string): Promise<TrekPackage[]> {
    return getPackagesByCompanyId(companyId);
  }

  async getPackageById(packageId: string): Promise<TrekPackage | null> {
    return getPackageById(packageId);
  }

  async searchTreks(query: string): Promise<Trek[]> {
    return searchTreks(query);
  }

  async getCommonSearchTerms(): Promise<string[]> {
    return getCommonSearchTerms();
  }

  async getSearchSuggestions(query: string): Promise<string[]> {
    return getSearchSuggestions(query);
  }
  
  async recordSuggestionClick(suggestion: string): Promise<void> {
    return recordSuggestionClick(suggestion);
  }
  
  async submitContactInquiry(formData: ContactFormData): Promise<{ error?: Error }> {
    return submitContactInquiry(formData);
  }
  
  async getContactInquiries(page = 1, pageSize = 10, statusFilter: InquiryStatus | 'all' = 'all'): 
    Promise<{ data: ContactInquiry[], count: number | null, error?: Error }> {
    return getContactInquiries(page, pageSize, statusFilter);
  }
  
  async updateInquiryStatus(id: string, status: InquiryStatus): 
    Promise<{ data: ContactInquiry | null, error?: Error }> {
    return updateInquiryStatus(id, status);
  }

  async recordSearchQuery(query: string, resultsCount: number, source: string): Promise<void> {
    try {
      // Use direct SQL query instead of RPC
      await supabase
        .from('search_analytics')
        .insert({
          query,
          results_count: resultsCount,
          source
        });
      console.log(`Recording search query: ${query} with ${resultsCount} results from ${source}`);
    } catch (error) {
      console.error('Error recording search query:', error);
    }
  }

  async getTrendingSearchTerms(limit = 10, days = 30): Promise<{term: string, count: number}[]> {
    try {
      // Use SQL function instead of query builder with group
      const { data, error } = await supabase.rpc('get_trending_search_terms', {
        limit_count: limit,
        days_lookback: days
      });
      
      if (error) {
        console.error('Error fetching trending search terms:', error);
        return [];
      }
      
      return data as {term: string, count: number}[];
    } catch (error) {
      console.error('Error fetching trending search terms:', error);
      return [];
    }
  }

  async getZeroResultSearchTerms(limit = 10, days = 30): Promise<{term: string, count: number}[]> {
    try {
      // Use SQL function instead of query builder with group
      const { data, error } = await supabase.rpc('get_zero_result_search_terms', {
        limit_count: limit,
        days_lookback: days
      });
      
      if (error) {
        console.error('Error fetching zero result search terms:', error);
        return [];
      }
      
      return data as {term: string, count: number}[];
    } catch (error) {
      console.error('Error fetching zero result search terms:', error);
      return [];
    }
  }

  async getLocationSearchAnalytics(): Promise<{location: string, count: number}[]> {
    try {
      // Use SQL function instead of query builder with group
      const { data, error } = await supabase.rpc('get_location_search_analytics');
      
      if (error) {
        console.error('Error fetching location search analytics:', error);
        return [];
      }
      
      return data as {location: string, count: number}[];
    } catch (error) {
      console.error('Error fetching location search analytics:', error);
      return [];
    }
  }
}

export const supabaseClient = new SupabaseApiClient();
